// LocalStorage utility for managing app data
//
// Usage Examples:
//
// Basic operations:
// setStorage('user_preferences', { theme: 'dark', language: 'en' })
// getStorage('user_preferences') // Returns: { theme: 'dark', language: 'en' }
// removeStorage('user_preferences')
// hasStorage('user_preferences') // Returns: false
//
// With expiry (24 hours):
// setStorage('temp_data', 'some value', 24 * 60 * 60 * 1000)
//
// Multiple operations:
// setMultipleStorage({ key1: 'value1', key2: 'value2' })
// getMultipleStorage(['key1', 'key2']) // Returns: { key1: 'value1', key2: 'value2' }
//
// Auth operations:
// setAuth('token123', 'user', 24 * 60 * 60 * 1000) // Save with expiry
// setAuth('token123') // Save without authType (for sign in)
// getAuth() // Returns: { token: 'token123', type: 'user', timestamp: 1234567890 }
// getAuthToken() // Returns: 'token123'
// getAuthType() // Returns: 'user' or null
// isAuthenticated() // Returns: true/false
// clearAuth() // Clears all auth data
//
// Storage management:
// clearExpiredStorage() // Removes expired items
// clearAllStorage() // Removes all app data
// getStorageInfo() // Returns storage usage statistics
//
// Debug (available in browser console):
// window.storageManager - Access to all methods
// window.setStorage, window.getStorage, etc. - Individual functions

class StorageManager {
  constructor() {
    this.prefix = "flirto_";
    this.defaultExpiry = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  }

  // Set a value with optional expiry
  set(key, value, expiry = null) {
    try {
      const data = {
        value,
        timestamp: Date.now(),
        expiry: expiry ? Date.now() + expiry : null,
      };
      localStorage.setItem(this.prefix + key, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error("Error setting localStorage item:", error);
      return false;
    }
  }

  // Get a value, returns null if expired or not found
  get(key) {
    try {
      const item = localStorage.getItem(this.prefix + key);
      if (!item) return null;

      const data = JSON.parse(item);

      // Check if expired
      if (data.expiry && Date.now() > data.expiry) {
        this.remove(key);
        return null;
      }

      return data.value;
    } catch (error) {
      console.error("Error getting localStorage item:", error);
      return null;
    }
  }

  // Remove a specific key
  remove(key) {
    try {
      localStorage.removeItem(this.prefix + key);
      return true;
    } catch (error) {
      console.error("Error removing localStorage item:", error);
      return false;
    }
  }

  // Check if a key exists and is not expired
  has(key) {
    return this.get(key) !== null;
  }

  // Set multiple values at once
  setMultiple(items, expiry = null) {
    const results = {};
    Object.entries(items).forEach(([key, value]) => {
      results[key] = this.set(key, value, expiry);
    });
    return results;
  }

  // Get multiple values at once
  getMultiple(keys) {
    const results = {};
    keys.forEach((key) => {
      results[key] = this.get(key);
    });
    return results;
  }

  // Clear all expired items
  clearExpired() {
    try {
      const keys = Object.keys(localStorage);
      let clearedCount = 0;

      keys.forEach((key) => {
        if (key.startsWith(this.prefix)) {
          const item = localStorage.getItem(key);
          if (item) {
            try {
              const data = JSON.parse(item);
              if (data.expiry && Date.now() > data.expiry) {
                localStorage.removeItem(key);
                clearedCount++;
              }
            } catch (error) {
              // Invalid JSON, remove it
              localStorage.removeItem(key);
              clearedCount++;
            }
          }
        }
      });

      return clearedCount;
    } catch (error) {
      console.error("Error clearing expired items:", error);
      return 0;
    }
  }

  // Clear all app data
  clearAll() {
    try {
      const keys = Object.keys(localStorage);
      let clearedCount = 0;

      keys.forEach((key) => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
          clearedCount++;
        }
      });

      return clearedCount;
    } catch (error) {
      console.error("Error clearing all app data:", error);
      return 0;
    }
  }

  // Get storage usage info
  getStorageInfo() {
    try {
      const keys = Object.keys(localStorage);
      const appKeys = keys.filter((key) => key.startsWith(this.prefix));

      let totalSize = 0;
      let expiredCount = 0;
      let validCount = 0;

      appKeys.forEach((key) => {
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += item.length;
          try {
            const data = JSON.parse(item);
            if (data.expiry && Date.now() > data.expiry) {
              expiredCount++;
            } else {
              validCount++;
            }
          } catch (error) {
            expiredCount++;
          }
        }
      });

      return {
        totalKeys: appKeys.length,
        validKeys: validCount,
        expiredKeys: expiredCount,
        totalSize: totalSize,
        totalSizeKB: (totalSize / 1024).toFixed(2),
      };
    } catch (error) {
      console.error("Error getting storage info:", error);
      return null;
    }
  }

  // Set auth data (special method for authentication)
  setAuth(authToken, authType = null, expiry = null) {
    const authData = {
      token: authToken,
      type: authType,
      timestamp: Date.now(),
    };

    this.set("auth", authData, expiry);
    this.set("authToken", authToken, expiry);

    if (authType) {
      this.set("authType", authType, expiry);
    }
  }

  // Get auth data
  getAuth() {
    return this.get("auth");
  }

  // Get auth token
  getAuthToken() {
    return this.get("authToken");
  }

  // Get auth type
  getAuthType() {
    return this.get("authType");
  }

  // Clear auth data
  clearAuth() {
    this.remove("auth");
    this.remove("authToken");
    this.remove("authType");
  }

  // Check if user is authenticated
  isAuthenticated() {
    return this.has("authToken");
  }
}

// Create singleton instance
const storageManager = new StorageManager();

// Export functions
export const setStorage = (key, value, expiry) =>
  storageManager.set(key, value, expiry);
export const getStorage = (key) => storageManager.get(key);
export const removeStorage = (key) => storageManager.remove(key);
export const hasStorage = (key) => storageManager.has(key);
export const setMultipleStorage = (items, expiry) =>
  storageManager.setMultiple(items, expiry);
export const getMultipleStorage = (keys) => storageManager.getMultiple(keys);
export const clearExpiredStorage = () => storageManager.clearExpired();
export const clearAllStorage = () => storageManager.clearAll();
export const getStorageInfo = () => storageManager.getStorageInfo();

// Auth-specific functions
export const setAuth = (authToken, authType, expiry) =>
  storageManager.setAuth(authToken, authType, expiry);
export const getAuth = () => storageManager.getAuth();
export const getAuthToken = () => storageManager.getAuthToken();
export const getAuthType = () => storageManager.getAuthType();
export const clearAuth = () => storageManager.clearAuth();
export const isAuthenticated = () => storageManager.isAuthenticated();

// Make available globally for debugging
if (typeof window !== "undefined") {
  window.storageManager = storageManager;
  window.setStorage = setStorage;
  window.getStorage = getStorage;
  window.removeStorage = removeStorage;
  window.setAuth = setAuth;
  window.getAuth = getAuth;
  window.clearAuth = clearAuth;
  window.isAuthenticated = isAuthenticated;
}
